import type { ReactNode } from 'react';
import { type Theme, ThemeProvider } from '@material-ui/core/styles';

import {
  BackstageSidebarItemOverrides,
  BackstageSidebarOverrides,
} from '../components/sidebar-item/backstage-sidebar-item-overrides';
import { BackstageSidebarSubmenuItemOverrides } from '../components/sidebar-submenu/backstage-sidebar-submenu-item-overrides';

// MUI v4's Overrides type has no Backstage keys, and the override objects use
// JSS values its CSS types reject (`'700 !important'`). Spreading them from a
// constant keeps them out of the excess-property check, the same way the app
// theme passes its overrides.
const sidebarOverrides = {
  BackstageSidebar: BackstageSidebarOverrides,
  BackstageSidebarItem: BackstageSidebarItemOverrides,
  BackstageSidebarSubmenuItem: BackstageSidebarSubmenuItemOverrides,
};

// Module scope on purpose: MUI memoizes the merged theme on the identity of
// this function and the outer theme, so a stable function keeps one
// stylesheet per theme instead of one per render.
const withSidebarOverrides = (outerTheme: Theme): Theme => ({
  ...outerTheme,
  overrides: {
    ...outerTheme.overrides,
    ...sidebarOverrides,
  },
});

/** @public */
export interface SidebarThemeProviderProps {
  children: ReactNode;
}

/**
 * Lays the sidebar overrides over the app theme for everything inside it.
 *
 * @remarks
 * Place it around core's `Sidebar`, never inside it: the drawer and the
 * mobile bar read the theme in `Sidebar` itself. Needs an outer MUI v4
 * theme, which the app's UnifiedThemeProvider always provides. The
 * overrides are the same in light and dark, because they only reference
 * CSS variables, so the outer palette decides the mode.
 *
 * @public
 */
export const SidebarThemeProvider = ({
  children,
}: SidebarThemeProviderProps) => (
  <ThemeProvider theme={withSidebarOverrides}>{children}</ThemeProvider>
);
